import React, { useState, useEffect } from "react";
import Router from "@components/router.js";
import whatsapp from "@images/whatsapp.svg";
import email from "@images/email.svg";
import phone from "@images/phone.svg";
import contacts from "@images/contacts.svg";
import extraLink from "@images/extra_link.svg";
import telegram from "@images/telegram.svg";
import birthday from "@images/birthday.png";
import openEye from "@images/open_eye.svg";
import closedEye from "@images/closed_eye.svg";
import browser from "@images/browser.svg";
import facebook from "@images/facebook.svg";
import instagram from "@images/instagram.svg";
import vk from "@images/vk.svg";
import tiktok from "@images/tiktok.svg";
import linkedin from "@images/linkedin.svg";
import youtube from "@images/youtube.svg";
import Popover from "../Popover/Popover";

const Edit = ({ data }) => {
  const [formData, setFormData] = useState(data || {});
  const [validationErrors, setValidationErrors] = useState({});
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (notification) {
      const timeout = setTimeout(() => setNotification(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [notification]);

  const initializeField = (fieldId) => {
    if (!formData[fieldId]) {
      return { value: "", visible: true };
    }
    return formData[fieldId];
  };

  const validateUrl = (url) => {
    if (!url) return true;

    return /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/[^\s@]*)?$/i.test(url);
  };

  const validatePhone = (phone) => {
    if (!phone) return true;
    return /^[\d+\p{L}][\d() \p{L}-]{4,14}\d$/u.test(phone);
  };

  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateWhatsApp = (input) => {
    if (!input) return true;
    return /^[\d()+\sA-Za-z-]+([А-Яа-яЁё]+)?([A-Za-zА-Яа-яЁё0-9\s]+)?(\?text=.*)?$/u.test(
      input
    );
  };

  const toggleVisibility = (fieldId) => {
    Router.fetchToggleVisibility(fieldId, (response) => {
      if (response?.success || response?.visible !== undefined) {
        setFormData((prev) => ({
          ...prev,
          [fieldId]: {
            ...initializeField(fieldId),
            ...prev[fieldId],
            visible: !prev[fieldId]?.visible,
          },
        }));
      } else {
        setNotification({
          type: "error",
          message: I18n.t("profiles.update.visibility_error"),
        });
      }
    });
  };

  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: {
        ...initializeField(fieldId),
        ...prev[fieldId],
        value,
      },
    }));

    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (fieldId, checked) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: {
        ...initializeField(fieldId),
        ...prev[fieldId],
        value: checked,
      },
    }));
  };

  const fieldsWithEye = [
    "website",
    "website_second",
    "facebook",
    "instagram",
    "telegram",
    "vk",
    "tiktok",
    "linkedin",
    "youtube",
    "custom_link",
    "phone",
    "whatsapp",
    "email",
  ];

  const handleSubmit = (event) => {
    event.preventDefault();

    const errors = {};
    let hasErrors = false;

    const allFields = {
      urlFields: [
        "website",
        "website_second",
        "facebook",
        "instagram",
        "telegram",
        "vk",
        "tiktok",
        "linkedin",
        "youtube",
        "custom_link",
      ],
      phoneFields: ["phone"],
      emailFields: ["email"],
      whatsappFields: ["whatsapp"],
    };

    allFields.urlFields.forEach((field) => {
      const fieldValue = formData[field]?.value;
      if (fieldValue && !validateUrl(fieldValue)) {
        errors[field] = I18n.t("profiles.show.field.invalid_url");
        hasErrors = true;
      }
    });

    allFields.phoneFields.forEach((field) => {
      const fieldValue = formData[field]?.value;
      if (fieldValue && !validatePhone(fieldValue)) {
        errors[field] = I18n.t("profiles.show.field.invalid_phone");
        hasErrors = true;
      }
    });

    allFields.emailFields.forEach((field) => {
      const fieldValue = formData[field]?.value;
      if (fieldValue && !validateEmail(fieldValue)) {
        errors[field] = I18n.t("profiles.show.field.invalid_email");
        hasErrors = true;
      }
    });

    allFields.whatsappFields.forEach((field) => {
      const fieldValue = formData[field]?.value;
      if (fieldValue && !validateWhatsApp(fieldValue)) {
        errors[field] = I18n.t("profiles.show.field.invalid_phone");
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    if (hasErrors) {
      setNotification({
        type: "error",
        message: I18n.t("profiles.update.updated_error"),
      });
      return;
    }

    const profileData = {};

    Object.entries(formData).forEach(([key, { value, visible }]) => {
      if (value !== undefined || visible !== undefined) {
        profileData[key] = value;
      }
    });

    Router.fetchProfileEdit(
      "/profile.json",
      JSON.stringify({ profile: profileData }),
      (response) => {
        if (response?.success) {
          setNotification({
            type: "success",
            message: I18n.t("profiles.update.updated"),
          });
        } else {
          setNotification({
            type: "error",
            message: I18n.t("profiles.update.updated_error"),
          });
        }
      }
    );
  };

  const ErrorPopover = ({ error }) => (
    <div className="error-popover">
      <div className="error-popover-content">{error}</div>
    </div>
  );

  const renderInputWithEye = (
    id,
    icon,
    placeholder,
    type = "text",
    showPopover = false,
    popoverContent = ""
  ) => {
    const fieldData = formData[id] || {};
    const value = fieldData.value || "";
    const isVisible = fieldData.visible !== false;
    const error = validationErrors[id];

    useEffect(() => {
      if (error) {
        const timer = setTimeout(() => {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[id];
            return newErrors;
          });
        }, 3000);
        return () => clearTimeout(timer);
      }
    }, [error, id]);

    return (
      <div className="row-div" style={{ position: "relative" }}>
        <div className="edit-input-wrapper">
          <img src={icon} alt={id} className="edit-input-icon" />
          <input
            type={isVisible ? "text" : type}
            className={`edit-input ${type === "tel" ? "edit-input--tel" : ""}`}
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleChange(id, e.target.value)}
            name={id}
          />
          {value && fieldsWithEye.includes(id) && (
            <div
              className={
                showPopover ? "eye-wrapper-with-popover" : "eye-wrapper"
              }
              onClick={() => toggleVisibility(id)}
            >
              <img
                src={isVisible ? openEye : closedEye}
                alt={isVisible ? "open" : "close"}
                className="eye-icon"
              />
            </div>
          )}
        </div>
        {error && (
          <div className="error-popover">
            <div className="error-popover-content">{error}</div>
          </div>
        )}
        {showPopover && popoverContent && <Popover content={popoverContent} />}
      </div>
    );
  };

  return (
    <>
      {notification && (
        <div
          className="edit-notification"
          style={{
            backgroundColor:
              notification.type === "success" ? "#48bb78" : "#f56565",
          }}
        >
          {notification.message}
        </div>
      )}

      <form className="edit" onSubmit={handleSubmit}>
        <label className="edit-label">
          {I18n.t("profiles.field.labels.full_name")}
        </label>
        <div className="edit-input-wrapper">
          <img
            src={contacts}
            alt="contacts"
            className="edit-input-icon-contact"
          />
          <input
            type="text"
            className="edit-input"
            style={{ marginBottom: 10 }}
            value={formData.full_name.value}
            onChange={(e) => handleChange("full_name", e.target.value)}
            name="full_name"
          />
        </div>

        <label className="edit-label">
          {I18n.t("profiles.field.labels.company")}
        </label>
        <div className="edit-input-wrapper">
          <img
            src={contacts}
            alt="contacts"
            className="edit-input-icon-contact"
          />
          <input
            type="text"
            className="edit-input"
            style={{ marginBottom: 10 }}
            value={formData.company.value}
            onChange={(e) => handleChange("company", e.target.value)}
            name="company"
          />
        </div>

        <label className="edit-label">
          {I18n.t("profiles.field.labels.position")}
        </label>
        <div className="edit-input-wrapper">
          <img
            src={contacts}
            alt="contacts"
            className="edit-input-icon-contact"
          />
          <input
            type="text"
            className="edit-input"
            style={{ marginBottom: 10 }}
            value={formData.position.value}
            onChange={(e) => handleChange("position", e.target.value)}
            name="position"
          />
        </div>

        <label className="edit-label">
          {I18n.t("profiles.field.labels.birthday")}
        </label>
        <div className="edit-input-wrapper">
          <img src={birthday} alt="birthday" className="edit-birthday-icon" />
          <input
            type="date"
            className="edit-input edit-input--date"
            style={{ marginBottom: 10 }}
            value={formData.birthday.value}
            onChange={(e) => handleChange("birthday", e.target.value)}
            name="birthday"
          />
        </div>
        <div className="edit-checkbox-wrapper" style={{ marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={formData.short_birthday.value}
            onChange={(e) =>
              handleCheckboxChange("short_birthday", e.target.checked)
            }
            name="short_birthday"
          />
          <label>{formData.short_birthday.title}</label>
        </div>

        <label className="edit-label">
          {I18n.t("profiles.field.labels.phone")}
        </label>
        {renderInputWithEye(
          "phone",
          phone,
          "+79160001122",
          "tel",
          true,
          I18n.t("profiles.field.phone")
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.work_phone")}
        </label>
        {renderInputWithEye("work_phone", phone, "+79160001122", "tel")}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.email")}
        </label>
        {renderInputWithEye(
          "email",
          email,
          "mail@gmail.com",
          "email",
          true,
          I18n.t("profiles.field.email")
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.email_second")}
        </label>
        {renderInputWithEye("email_second", email, "mail@gmail.com", "email")}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.website")}
        </label>
        {renderInputWithEye("website", browser, "http://yourcf.ru")}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.website_second")}
        </label>
        {renderInputWithEye("website_second", browser, "http://yourcf.online")}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.facebook")}
        </label>
        {renderInputWithEye(
          "facebook",
          facebook,
          "http://facebook.com/yourcfuture"
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.instagram")}
        </label>
        {renderInputWithEye(
          "instagram",
          instagram,
          "http://instagram.com/your.cf/"
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.telegram")}
        </label>
        {renderInputWithEye(
          "telegram",
          telegram,
          "http://t.me/yourcf",
          "text",
          true,
          I18n.t("profiles.field.telegram")
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.whatsapp")}
        </label>
        {renderInputWithEye(
          "whatsapp",
          whatsapp,
          "79160001122",
          "text",
          true,
          I18n.t("profiles.field.whatsapp")
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.vk")}
        </label>
        {renderInputWithEye("vk", vk, "http://vk.com/yourcfuture")}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.tiktok")}
        </label>
        {renderInputWithEye("tiktok", tiktok, "http://tiktok.com/@yourcf")}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.linkedin")}
        </label>
        {renderInputWithEye(
          "linkedin",
          linkedin,
          "http://linkedin.com/in/yourlink/"
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.youtube")}
        </label>
        {renderInputWithEye(
          "youtube",
          youtube,
          "https://www.youtube.com/channel/UCsg_ejViMg8Z7TJz6tVz5mQ"
        )}

        <label className="edit-label">
          {I18n.t("profiles.field.labels.custom_link")}
        </label>
        <div className="edit-input-wrapper" style={{ flex: 1 }}>
          <img src={extraLink} alt="extra-link" className="edit-input-icon" />
          <input
            type="text"
            className="edit-input"
            placeholder="http://yourcf.ru"
            value={formData.custom_link.value}
            onChange={(e) => handleChange("custom_link", e.target.value)}
            name="custom_link"
          />
          {formData.custom_link.value && (
            <div
              className="eye-wrapper"
              onClick={() => toggleVisibility("custom_link")}
            >
              <img
                src={formData.custom_link.visible ? openEye : closedEye}
                alt={formData.custom_link.visible ? "open" : "close"}
                className="eye-icon"
              />
            </div>
          )}
          {validationErrors.custom_link && (
            <ErrorPopover error={validationErrors.custom_link} />
          )}
        </div>

        <label className="edit-label" style={{ marginTop: 10 }}>
          {I18n.t("profiles.field.labels.bio")}
        </label>
        <div className="row-div-textarea">
          <div className="edit-input-wrapper" style={{ flex: 1 }}>
            <img src={contacts} alt="contacts" className="edit-textarea-icon" />
            <textarea
              className="edit-textarea"
              value={formData.bio.value}
              onChange={(e) => handleChange("bio", e.target.value)}
              name="bio"
            />
          </div>
          <Popover content={I18n.t("profiles.field.bio")} />
        </div>

        <div className="row-div">
          <button className="save-button" type="submit">
            {I18n.t("form.save")}
          </button>
        </div>
      </form>
    </>
  );
};

export default Edit;
